import React, { useMemo } from 'react';
import { GameCarousel } from '@/components/games/game-carousel';
import offlineBannerBig from '@/assets/images/banners/qp-offline-big.webp';
import streamBannerBig from '@/assets/images/banners/qp-stream-big.webp';
import offlineBannerSmall from '@/assets/images/banners/qp-offline-small.webp';
import streamBannerSmall from '@/assets/images/banners/qp-stream-small.webp';
import type { CategoriesGames } from '@/types/category';

interface CategoriesWithBannersProps {
  categories: CategoriesGames[];
}

const Banner: React.FC<{
  variant: 'offline' | 'stream';
  index: number;
}> = ({ variant, index }) => {
  const urlMap = {
    offline:
      'https://quizplease.ru/schedule?QpGameSearch%5BcityId%5D=9&QpGameSearch%5Bdates%5D=&QpGameSearch%5Bstatus%5D%5B%5D=all&QpGameSearch%5Bformat%5D%5B%5D=0&QpGameSearch%5Btype%5D%5B%5D=all&QpGameSearch%5Bgame_difficulty%5D%5B%5D=all&QpGameSearch%5Bbars%5D%5B%5D=all&utm_source=qphome&utm_medium=banner_offline&utm_campaign=banner_offline1',
    stream:
      'https://quizplease.me/stream-page?utm_source=qphome&utm_medium=banner_stream&utm_campaign=banner_stream1',
  } as const;

  const href = urlMap[variant];
  const smallSrc = variant === 'offline' ? offlineBannerSmall : streamBannerSmall;
  const bigSrc = variant === 'offline' ? offlineBannerBig : streamBannerBig;
  const alt = variant === 'offline' ? 'Offline Gaming' : 'Stream Gaming';

  return (
    <a
      key={`${variant}-banner-${index}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full cursor-pointer rounded-md overflow-hidden"
    >
      <img loading="lazy" src={smallSrc} alt={alt} className="w-full h-auto sm:hidden" />
      <img loading="lazy" src={bigSrc} alt={alt} className="w-full h-auto hidden sm:block" />
    </a>
  );
};

export function CategoriesWithBanners({ categories }: CategoriesWithBannersProps) {
  const items = useMemo(() => {
    let hasShownOffline = false;
    let hasShownStream = false;

    return categories.flatMap((cat, i) => {
      const nodes: React.ReactNode[] = [
        <GameCarousel
          key={`cat-${cat.id}`}
          categoryId={cat.id}
          title={cat.name}
          games={cat.games}
        />,
      ];

      if ((i + 1) % 4 === 2 && !hasShownOffline) {
        hasShownOffline = true;
        nodes.push(<Banner key={`banner-${cat.id}-offline`} variant="offline" index={i} />);
      } else if ((i + 1) % 4 === 0 && !hasShownStream) {
        hasShownStream = true;
        nodes.push(<Banner key={`banner-${cat.id}-stream`} variant="stream" index={i} />);
      }

      return nodes;
    });
  }, [categories]);

  return <>{items}</>;
}
